import clsx from 'clsx';
import React from 'react';
import { useParams } from 'react-router';

import { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import Spinner from '@/src/components/common/Elements/Spinner/Spinner';
import ModalPortal from '@/src/components/common/Modal/ModalPortal';
import { isResponseError } from '@/src/components/project/Inbox2/api/config';
import ConfirmArchiveDocumentModal from '@/src/components/project/Inbox2/components/EntryPanel/FileEntryViewer/components/SidebarActions/DeleteFile/ArchiveDocumentModal/ConfirmArchiveDocumentModal/ConfirmArchiveDocumentModal';
import ActionButton from '@/src/components/project/Inbox2/Elements/ActionButton/ActionButton';
import { useInboxEntryPanel } from '@/src/components/project/Inbox2/store/inboxEntryPanel.store';
import { EmailEntry } from '@/src/components/project/Inbox2/types';
import useInboxSearchParams from '@/src/components/project/Inbox2/useInboxSearchParams';
import { showToast } from '@/src/globalFunction';
import { useToggle } from '@/src/hooks/useToggle';

import { useValidateActionResponse } from '../../../../../useValidateActionResponse';
import { getCurrentEmailEntriesCountFromCache } from '../../../../api/getEmailEntries';
import { usePermanentDeleteEmail } from '../../../../api/permanentDeleteEmail';
import { useSyncSelectedEmailEntryListItem } from '../../EmailEntries/useSyncSelectedEmailEntryListItem';

const subject = 'file';

type PermanentDeleteEmailProps = {
  selectedEmailEntry: EmailEntry;
  enabled: boolean;
};

const PermanentDeleteEmail = ({
  selectedEmailEntry,
  enabled = true,
}: PermanentDeleteEmailProps) => {
  const { projectId } = useParams<{ projectId: string }>();
  const [showConfirmationModal, toggleShowConfirmationModal] = useToggle(false);
  const { setInboxEntry } = useInboxEntryPanel();
  const permanentDeleteEmailMutation = usePermanentDeleteEmail({
    projectId,
    inboxEntryId: selectedEmailEntry.inboxEntryId,
  });
  const { confirmRefreshModal } = useValidateActionResponse({
    responseError: isResponseError(permanentDeleteEmailMutation.data)
      ? permanentDeleteEmailMutation.data
      : null,
  });
  const syncEmailEntries = useSyncSelectedEmailEntryListItem({ emailEntry: selectedEmailEntry });
  const { filter } = useInboxSearchParams();

  const handlePermanentDelete = async () => {
    const response = await permanentDeleteEmailMutation.mutateAsync({
      projectId,
      inboxEntryId: selectedEmailEntry.inboxEntryId,
      emailId: selectedEmailEntry.id,
    });

    if (response) {
      toggleShowConfirmationModal(false);

      const currentEmailEntriesCount = getCurrentEmailEntriesCountFromCache({
        projectId,
        filter,
        inboxEntryId: selectedEmailEntry.inboxEntryId,
      });

      if (currentEmailEntriesCount <= 1) {
        setInboxEntry(null);
      } else {
        syncEmailEntries();
      }
      showToast('success', `Email has been Deleted.'`);
    }
  };

  return (
    <React.Fragment>
      {permanentDeleteEmailMutation.isLoading && <Spinner />}
      <ActionButton
        id={'delete_file'}
        className={clsx('tw-text-cfo-red-400', 'hover:tw-bg-red-100')}
        icon={ICONS.delete}
        iconClasses={clsx('tw-text-cfo-red-400', 'group-hover:tw-text-cfo-red-400')}
        onClick={toggleShowConfirmationModal}
        tooltipSettings={{
          content: 'Delete',
          place: 'left',
        }}
        disabled={!enabled}
      />

      {showConfirmationModal && (
        <ModalPortal>
          <ConfirmArchiveDocumentModal
            subject={subject}
            bodySubject={subject}
            onSubmit={handlePermanentDelete}
            onClose={toggleShowConfirmationModal}
            permanentDelete={true}
          />
        </ModalPortal>
      )}
      {confirmRefreshModal}
    </React.Fragment>
  );
};

export default PermanentDeleteEmail;
